import { aiUsageForWorkspace, generateInsights } from 'api/aiModalApi';
import { useEffect, useState, useCallback, useMemo } from 'react';

interface AIUsageData {
  has: boolean;
  message: string;
  remainingUsage?: number;
}

interface AIInsightsData {
  insights: string;
  contextId: string;
}

function useAI() {
  const [aiUsageData, setAIUsageData] = useState<AIUsageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [insights, setInsights] = useState<string>('');
  const [contextId, setContextId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Memoize the AI usage fetching function
  const getAIUsage = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await aiUsageForWorkspace();
      setAIUsageData(response as AIUsageData);
      return response;
    } catch (error) {
      console.error('Failed to fetch AI usage:', error);
      setError('Failed to fetch AI usage data');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch AI usage data on mount
  useEffect(() => {
    getAIUsage();
  }, [getAIUsage]);

  // Helper function to check if AI usage is allowed
  const canUseAI = useMemo(() => {
    return aiUsageData?.has === true;
  }, [aiUsageData]);

  // Helper function to get usage message
  const getUsageMessage = useCallback(() => {
    return aiUsageData?.message || '';
  }, [aiUsageData]);

  // Helper function to get remaining usage count
  const getRemainingUsage = useCallback(() => {
    return aiUsageData?.remainingUsage || null;
  }, [aiUsageData]);

  // Generate insights function
  const generateAIInsights = useCallback(
    async (contextType: string, contextData: any) => {
      try {
        setIsGenerating(true);
        setError(null);
        const response = await generateInsights({
          contextType,
          contextData,
        });

        if (response?.data?.insights) {
          setInsights(response?.data?.insights);
          setContextId(response?.data?.contextId);
          return response;
        }
        return null;
      } catch (error) {
        console.error('Failed to generate insights:', error);
        setError('Failed to generate insights');
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    [],
  );

  // Regenerate insights using existing context
  const regenerateAIInsights = useCallback(async () => {
    if (!contextId) {
      console.error('No context ID available for regeneration');
      return null;
    }

    try {
      setIsGenerating(true);
      setError(null);
      const response = await generateInsights({
        contextId,
      });

      if (response?.data?.insights) {
        setInsights(response?.data?.insights);
        return response;
      }
      return null;
    } catch (error) {
      console.error('Failed to regenerate insights:', error);
      setError('Failed to regenerate insights');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [contextId]);

  // Clear insights
  const clearInsights = useCallback(() => {
    setInsights('');
    setContextId(null);
    setError(null);
  }, []);

  return {
    aiUsageData,
    isLoading,
    error,
    canUseAI,
    getUsageMessage,
    getRemainingUsage,
    refreshAIUsage: getAIUsage,
    insights,
    contextId,
    isGenerating,
    generateAIInsights,
    regenerateAIInsights,
    clearInsights,
  };
}

export default useAI;
